import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../../styled-components/dark';

const Wrapper = styled.div`
  position: relative;
  background-color: transparent;
  width: 8em;
  height: 1.8em;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const Inner = styled.div`
  position: relative;

  width: 100%;
  height: 100%;
  padding: 2px;
  border-radius: 9999px;

  background: ${theme.MissionProgressBar.bgGradient};

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    border-radius: inherit;
    background: inherit;

    filter: blur(0.4em);
  }
`;

const Border = styled.div`
  position: relative;

  width: 100%;
  height: 100%;
  border-radius: inherit;
  background-color: black;

  overflow: hidden;
`;

const BORDER_THICKNESS = 2; // px

const ProgressBackground = styled.div`
  position: absolute;

  top: ${BORDER_THICKNESS}px;
  left: ${BORDER_THICKNESS}px;

  width: calc(100% - ${2 * BORDER_THICKNESS}px);
  height: calc(100% - ${2 * BORDER_THICKNESS}px);
  border-radius: inherit;
  background-color: ${theme.MissionProgressBar.progressBarBgColor};
  overflow: hidden;
`;

type ProgressBarType = {
  $xRatio: number;
};

const ProgressBar = styled.div<ProgressBarType>`
  position: absolute;
  width: calc(100%);
  height: calc(100%);
  top: 0;
  left: 0;

  background-color: ${theme.MissionProgressBar.progressBarColor};

  transition: transform 1500ms ease-in-out;

  &:before {
    content: '';
    position: absolute;
    border-left: 0.52em solid transparent;
    border-bottom: 1.9em solid #313444;
    left: -0.45em;
  }

  ${({ $xRatio }) => {
    let offsetX = 0;

    if ($xRatio === 1) {
      offsetX = 0.3;
    } else if (!$xRatio) {
      offsetX = 0;
    } else {
      offsetX = 0.15;
    }
    return css`
      transform: translateX(calc(${offsetX}em + ${$xRatio * 100}%));
    `;
  }}
`;

const LabelWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;

  text-align: center;

  display: flex;
  align-items: center;
  justify-content: center;
`;

type LabelType = {
  $barCompleted: boolean;
};

export const MissionProgressLabelClassName = 'mission-progress-label';
const Label = styled.div<LabelType>`
  font-size: 0.85em;
  font-weight: bold;

  ${({ $barCompleted }) =>
    $barCompleted
      ? css`
          color: black;
        `
      : css`
          color: white;
        `}

  transition: color 1500ms ease-in-out;
`;

type ClipType = {
  $widthRatio: number;
  $i: number;
};

const Clip = styled.div<ClipType>`
  position: absolute;
  height: 200%;
  width: 0.133em;
  left: calc(${({ $i, $widthRatio }) => ($i + 1) * ($widthRatio * 100)}% - ${BORDER_THICKNESS / 2}px);
  top: calc(50%);

  transform-origin: center center;
  transform: translateY(-50%) rotate(15deg);
  background-color: black;
`;

type MissionProgressBarrProps = {
  nbClips: number;
  nbCompleted: number;
  className?: string;
};

const MissionProgressBar = ({ nbClips, nbCompleted, className }: MissionProgressBarrProps) => {
  const progressBarXRatio = useMemo(() => nbCompleted / nbClips, [nbCompleted, nbClips]);
  const clips = useMemo(() => (nbClips ? [...new Array(nbClips - 1).keys()] : []), [nbClips]);

  return (
    <Wrapper className={className}>
      <Inner>
        <Border>
          <ProgressBackground>
            <ProgressBar $xRatio={progressBarXRatio} />
            {nbClips === 1 ? (
              <LabelWrapper>
                <Label className={MissionProgressLabelClassName} $barCompleted={nbClips === nbCompleted}>
                  {nbCompleted} / 1
                </Label>
              </LabelWrapper>
            ) : (
              clips.map((i) => <Clip key={`progressbar-clip-${i}`} $i={i} $widthRatio={1 / nbClips} />)
            )}
          </ProgressBackground>
        </Border>
      </Inner>
    </Wrapper>
  );
};

export default MissionProgressBar;
